import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import type { Card } from '../../types/board';

interface EditCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (content: string) => void | Promise<void>;
  card: Card | null;
  isLoading?: boolean;
}

export function EditCardModal({
  isOpen,
  onClose,
  onSave,
  card,
  isLoading = false,
}: EditCardModalProps): JSX.Element {
  const [content, setContent] = useState('');

  useEffect(() => {
    setContent(card?.content ?? '');
  }, [card]);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed || isLoading) return;
    void onSave(trimmed);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Card">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="cardContent"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Card Content
          </label>
          <textarea
            id="cardContent"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={5}
            autoFocus
            disabled={isLoading}
            className="w-full resize-none rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!content.trim() || isLoading}
            className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
